import React, { useMemo, useCallback } from 'react'
import { Calendar, dateFnsLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { format, parse, startOfWeek as startOfWeekFn, getDay } from 'date-fns'
import { ko } from 'date-fns/locale'

import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'

const locales = { ko }

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (d) => startOfWeekFn(d, { weekStartsOn: 0 }),
    getDay,
    locales,
})

const DnDCalendar = withDragAndDrop(Calendar)

const messages = {
    date: '날짜',
    time: '시간',
    event: '일정',
    allDay: '종일',
    week: '주',
    work_week: '주(근무)',
    day: '일',
    month: '월',
    previous: '이전',
    next: '다음',
    yesterday: '어제',
    tomorrow: '내일',
    today: '오늘',
    agenda: '목록',
    showMore: (n) => `+${n}개 더`,
    noEventsInRange: '이 구간에 표시할 일정이 없습니다.',
}

/**
 * @param {object[]} events API summary rows (id, title, startAt, endAt, allDay, cancelled, …)
 * @param {'month'|'week'|'day'|'agenda'} view
 * @param {Date} date
 * @param {(d: Date) => void} onNavigate
 * @param {(v: string) => void} onView
 * @param {(ev: object) => void} onSelectEvent
 * @param {(raw: object) => boolean} canEditEvent
 * @param {(args: { event: object, start: Date, end: Date }) => void | Promise<void>} onCommitRange
 * @param {(slotInfo: { start: Date, end: Date, slots: Date[], action: string }) => void} [onSelectSlot]
 * @param {boolean} [compact] 대시보드 등 좁은 영역용 높이·최소 높이 축소
 * @param {('month'|'week'|'day'|'agenda')[]} [views] 허용 뷰 목록(기본 월·주·일·목록)
 */
export default function WorkCalendarRbc({
    events,
    view,
    date,
    onNavigate,
    onView,
    onSelectEvent,
    canEditEvent,
    onCommitRange,
    onSelectSlot,
    compact = false,
    views = ['month', 'week', 'day', 'agenda'],
}) {
    const rbcEvents = useMemo(
        () =>
            (events || []).map((ev) => ({
                id: ev.occurrenceKey ? String(ev.occurrenceKey) : String(ev.id),
                title: `${ev.cancelled ? '[취소] ' : ''}${ev.title || ''}${ev.rrule ? ' ⟳' : ''}`,
                start: new Date(ev.startAt),
                end: new Date(ev.endAt),
                allDay: !!ev.allDay,
                resource: ev,
            })),
        [events],
    )

    const draggableAccessor = useCallback((e) => canEditEvent(e.resource), [canEditEvent])
    const resizableAccessor = useCallback((e) => canEditEvent(e.resource) && !e.resource?.cancelled, [canEditEvent])

    const eventPropGetter = useCallback((e) => {
        const raw = e.resource
        const classes = []
        if (raw?.cancelled) {
            classes.push('rbc-event-cancelled')
            return { className: classes.join(' ') }
        }
        if (raw?.eventKind === 'ORG') {
            classes.push('rbc-event-org')
        } else if (raw?.calendarLayer === 'TEAM_MEMBER_PERSONAL') {
            classes.push('rbc-event-team-personal')
        } else {
            const digest = raw?.autoDigestKind
            if (digest === 'INSURANCE_AGE_MATURITY') {
                classes.push('rbc-event-auto-ins')
            } else if (digest === 'CUSTOMER_BIRTHDAY') {
                classes.push('rbc-event-auto-birth')
            } else if (digest === 'CUSTOMER_ANNIVERSARY') {
                classes.push('rbc-event-auto-anniv')
            } else if (raw?.calendarSourceKind === 'CLAIM_DAILY') {
                classes.push('rbc-event-claim')
            } else {
                switch (String(raw?.personalCategory || '').trim().toUpperCase()) {
                    case 'MEETING':
                        classes.push('rbc-event-cat-meeting')
                        break
                    case 'CLIENT_MEETING':
                        classes.push('rbc-event-cat-client')
                        break
                    case 'TRIP':
                        classes.push('rbc-event-cat-trip')
                        break
                    case 'LEAVE':
                        classes.push('rbc-event-cat-leave')
                        break
                    case 'TRAINING':
                        classes.push('rbc-event-cat-training')
                        break
                    case 'OTHER':
                        classes.push('rbc-event-cat-other')
                        break
                    default:
                        classes.push('rbc-event-personal')
                }
            }
        }
        if (String(raw?.title || '').trim().startsWith('[연락]')) {
            classes.push('rbc-event-contact')
        }
        return { className: classes.join(' ') }
    }, [])

    const handleEventDrop = useCallback(
        async ({ event, start, end }) => {
            await onCommitRange?.({ event: event.resource, start, end })
        },
        [onCommitRange],
    )

    const handleEventResize = useCallback(
        async ({ event, start, end }) => {
            await onCommitRange?.({ event: event.resource, start, end })
        },
        [onCommitRange],
    )

    const minCal = compact ? 480 : 520
    const calHeight = compact ? 'min(560px, min(68vh, 780px))' : 'min(720px, calc(100vh - 240px))'

    return (
        <div className={`work-calendar-rbc rounded border border-slate-200 bg-white overflow-hidden p-1${compact ? ' work-calendar-rbc--compact' : ''}`}>
            <style>{`
                .work-calendar-rbc .rbc-calendar { min-height: ${minCal}px; }
                /* 월·주·일 공통: 셀 안에 제목이 더 많이 들어가도록 글자·여백 축소 */
                .work-calendar-rbc .rbc-event {
                    font-size: 11px;
                    line-height: 1.2;
                    padding: 1px 3px !important;
                    border-radius: 3px;
                }
                .work-calendar-rbc .rbc-event-content,
                .work-calendar-rbc .rbc-event-label {
                    font-size: inherit;
                    line-height: inherit;
                }
                .work-calendar-rbc .rbc-month-view .rbc-event-content {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .work-calendar-rbc .rbc-show-more {
                    font-size: 10px;
                    line-height: 1.2;
                    padding: 0 2px;
                }
                .work-calendar-rbc--compact .rbc-event,
                .work-calendar-rbc--compact .rbc-event-label {
                    font-size: 9px;
                }
                .work-calendar-rbc--compact .rbc-show-more {
                    font-size: 9px;
                }
                .work-calendar-rbc .rbc-event-org { background-color: #0369a1 !important; border-color: #075985 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-personal { background-color: #0d9488 !important; border-color: #0f766e !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-team-personal { background-color: #4f46e5 !important; border-color: #4338ca !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-auto-ins { background-color: #d97706 !important; border-color: #b45309 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-auto-birth { background-color: #e11d48 !important; border-color: #be123c !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-auto-anniv { background-color: #9333ea !important; border-color: #7e22ce !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-claim { background-color: #be123c !important; border-color: #9f1239 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-meeting { background-color: #2563eb !important; border-color: #1d4ed8 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-client { background-color: #0891b2 !important; border-color: #0e7490 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-trip { background-color: #059669 !important; border-color: #047857 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-leave { background-color: #64748b !important; border-color: #475569 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-training { background-color: #7c3aed !important; border-color: #6d28d9 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cat-other { background-color: #4b5563 !important; border-color: #374151 !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-contact.rbc-event-org { background-color: #c2410c !important; border-color: #9a3412 !important; }
                .work-calendar-rbc .rbc-event-contact:not(.rbc-event-org) { background-color: #ea580c !important; border-color: #c2410c !important; color: #fff !important; }
                .work-calendar-rbc .rbc-event-cancelled { opacity: 0.55; text-decoration: line-through; filter: grayscale(0.35); }
            `}</style>
            <DnDCalendar
                culture="ko"
                localizer={localizer}
                events={rbcEvents}
                view={view}
                date={date}
                onNavigate={onNavigate}
                onView={onView}
                views={views}
                startAccessor="start"
                endAccessor="end"
                style={{ height: calHeight }}
                messages={messages}
                onSelectEvent={(calEv) => onSelectEvent(calEv.resource)}
                resizable
                draggableAccessor={draggableAccessor}
                resizableAccessor={resizableAccessor}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                eventPropGetter={eventPropGetter}
                selectable={Boolean(onSelectSlot)}
                onSelectSlot={onSelectSlot}
            />
        </div>
    )
}
